/* Rollink duplicated theme only — experimental Shop Pay display sync.
 * This updates the product-page payment-terms *estimate*, not the Shopify
 * product price, actual financing offer, cart, or checkout.
 * Enable on live theme only after verifying real checkout behavior with Rollink.
 * No Shopify private APIs: relies on observed shopify-meta attribute behavior.
 */
(() => {
  'use strict';

  const SELECTOR = 'shopify-payment-terms';
  const RPD_SELECTOR = '[data-forus-storefront-pricing] .forus-native-price--pdp';
  const originals = new WeakMap();
  let scheduled = false;
  let observer;

  function moneyToCents(text) {
    const cleaned = String(text || '').replace(/[^\d.,]/g, '');
    if (!cleaned) return null;
    const dot = cleaned.lastIndexOf('.');
    const comma = cleaned.lastIndexOf(',');
    const last = Math.max(dot, comma);
    let normalized;
    if (last < 0) normalized = cleaned;
    else {
      const fraction = cleaned.length - last - 1;
      if (fraction === 2) {
        normalized = cleaned.slice(0, last).replace(/[.,]/g, '') + '.' + cleaned.slice(last + 1);
      } else {
        normalized = cleaned.replace(/[.,]/g, '');
      }
    }
    const n = Number(normalized);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  }

  function formatUSD(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function bandFor(cents, bands) {
    return bands.find((band) => {
      const min = moneyToCents(band.min_price);
      const max = moneyToCents(band.max_price);
      return min !== null && max !== null && cents >= min && cents <= max;
    });
  }

  function isShown(node) {
    if (!node || !node.isConnected) return false;
    const style = getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden' && node.getClientRects().length > 0;
  }

  function automaticRpdPrice() {
    const pdp = document.querySelector(RPD_SELECTOR);
    if (!isShown(pdp)) return null;
    // A visible automatic-promotion label is our positive signal. If the
    // merchant disables that label, fail closed rather than finance a coupon
    // which might not have been entered at checkout.
    if (!pdp.querySelector('.forus-promo-price-label') || pdp.querySelector('.forus-promo-code')) return null;
    const price = pdp.querySelector('.forus-promo-current');
    if (!price || !isShown(price)) return null;
    const cents = moneyToCents(price.textContent);
    return cents !== null && cents > 0 ? cents : null;
  }

  function readOriginal(el) {
    const current = el.getAttribute('shopify-meta');
    if (!current) return null;
    let state = originals.get(el);
    // Shopify/theme might have replaced the data after a variant change.
    if (!state || (current !== state.original && current !== state.applied)) {
      try {
        state = { original: current, applied: null, meta: JSON.parse(current) };
        originals.set(el, state);
      } catch (_) {
        return null;
      }
    }
    return state;
  }

  function restore(el, state) {
    if (state && state.applied !== null) {
      if (el.getAttribute('shopify-meta') === state.applied) {
        state.applied = null;
        el.setAttribute('shopify-meta', state.original);
      } else {
        // An external update occurred; do not overwrite it.
        state.applied = null;
      }
    }
  }

  function sync(el) {
    const state = readOriginal(el);
    if (!state) return;
    const amount = automaticRpdPrice();
    const meta = state.meta;
    const selectedId = el.getAttribute('variant-id');
    const variants = Array.isArray(meta.variants) ? meta.variants : [];
    const variant = variants.find((v) => String(v.id) === String(selectedId));
    const nativeCents = variant ? moneyToCents(variant.full_price) : null;
    const terms = variant ? Number(variant.number_of_payment_terms) : 0;
    const plans = Array.isArray(meta.financing_plans) ? meta.financing_plans : [];

    // Do not fabricate eligibility, terms, or prices when crossing plan bands.
    if (
      amount === null || !variant || nativeCents === null || !Number.isInteger(terms) || terms < 1 ||
      variant.eligible !== true || variant.available !== true ||
      meta.currency_code !== 'USD' || meta.country_code !== 'US' ||
      !bandFor(nativeCents, plans) ||
      bandFor(nativeCents, plans) !== bandFor(amount, plans) ||
      amount >= nativeCents
    ) {
      restore(el, state);
      return;
    }

    const updated = structuredClone(meta);
    const target = updated.variants.find((v) => String(v.id) === String(selectedId));
    target.full_price = formatUSD(amount);
    target.price_per_term = formatUSD(Math.round(amount / terms));
    const next = JSON.stringify(updated);
    if (el.getAttribute('shopify-meta') === next) return;
    // Track our own write so the observer never treats it as a new original.
    state.applied = next;
    el.setAttribute('shopify-meta', next);
  }

  function run() {
    scheduled = false;
    document.querySelectorAll(SELECTOR).forEach(sync);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(run);
  }

  function start() {
    schedule();
    observer = new MutationObserver((mutations) => {
      // Ignore mutations entirely inside the widget's shadow DOM; it is not
      // observed here. The one shopify-meta change we make is idempotent.
      if (mutations.some((m) => m.type === 'childList' ||
          (m.type === 'attributes' && ['shopify-meta', 'variant-id', 'style', 'class'].includes(m.attributeName)))) {
        schedule();
      }
    });
    observer.observe(document.documentElement, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['shopify-meta', 'variant-id', 'style', 'class']
    });
    window.addEventListener('pageshow', schedule);
    window.addEventListener('pagehide', () => {
      document.querySelectorAll(SELECTOR).forEach((el) => restore(el, originals.get(el)));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
